import { Injectable } from '@angular/core';
import {
  CriticalOrderResponse,
  NetworkHealthResponse,
  NewUpdatesResponse,
  TaskRecord,
  TasksResponse,
  TasksTotals,
} from '@core/models';
import { BehaviorSubject, Observable, combineLatest, map, switchMap, take, tap } from 'rxjs';
import { DashboardApiService } from './dashboard-api.service';
import { TaskStatusMap } from '@core/constants';
import { DataCollectionTemplateId } from '@core/enums';
import { TemplatesService } from '../templates/templates.service';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  readonly criticalOrders$ = new BehaviorSubject<CriticalOrderResponse | null>(null);

  readonly networkHealth$ = new BehaviorSubject<NetworkHealthResponse | null>(null);

  readonly newUpdates$ = new BehaviorSubject<NewUpdatesResponse | null>(null);

  get taskTotals$(): Observable<TasksTotals> {
    return this.#taskTotals$.asObservable();
  }
  get taskRecords$(): Observable<TaskRecord[]> {
    return this.#taskRecords$.asObservable();
  }
  readonly #taskTotals$ = new BehaviorSubject<TasksTotals>({
    assigned: 0,
    notReported: 0,
    failures: 0,
  });
  readonly #taskRecords$ = new BehaviorSubject<TaskRecord[]>([]);

  constructor(
    private dashboardApiService: DashboardApiService,
    private templatesService: TemplatesService
  ) {}

  getTasks(): Observable<TasksResponse> {
    return this.dashboardApiService.getTasks().pipe(
      switchMap((tasks: TasksResponse) =>
        this.templatesService.templates$.pipe(
          take(1),
          switchMap(() => {
            const transformedRecords$ = tasks.records.map(r =>
              this.templatesService.getDataCollectionTemplateName(r.template).pipe(
                take(1),
                map(templateName => ({
                  ...r,
                  templateId: r.template as DataCollectionTemplateId,
                  template: templateName,
                  type: r.type,
                  statusBadge: TaskStatusMap[r.status],
                }))
              )
            );

            return combineLatest(transformedRecords$).pipe(
              tap(transformedRecords => {
                this.#taskTotals$.next(tasks.totals);
                this.#taskRecords$.next(transformedRecords);
              }),
              map(() => tasks)
            );
          })
        )
      )
    );
  }

  getCriticalOrders(): Observable<CriticalOrderResponse> {
    return this.dashboardApiService.getCriticalOrders().pipe(
      tap((res) => this.criticalOrders$.next(res))
    );
  }

  getNetworkHealth(): Observable<NetworkHealthResponse> {
    return this.dashboardApiService.getNetworkHealth().pipe(
      tap((res) => this.networkHealth$.next(res))
    );
  }

  getNewUpdates(): Observable<NewUpdatesResponse> {
    return this.dashboardApiService.getNewUpdates().pipe(
      tap((res) => this.newUpdates$.next(res))
    );
  }
}
